import { DefaultMap, defined } from "@glideapps/ts-necessities";
import { isRow } from "../../computation-model/data";
import fromPairs from "lodash/fromPairs";

import { SerializedComponent } from "../../components/SerializedApp";
import { getFeatureSetting } from "../../feature-settings";
import { panic } from "../../Support";
import { makeValueForSourceColumn, SetColumnData, setColumnsInRowForEditComponent } from "../builder-utils";
import {
    AllowedDataEdits,
    ComponentBuilder,
    ComponentConfiguratorContext,
    EditedColumns,
    PropertyDescriptor,
    WireActionHydrator,
    WireInflationBackend,
    WireValueGetter,
} from "../components";
import {
    makePropertyDescriptorsForColumns,
    makeSingleRelationOrThisItemPropertyDescriptor,
} from "../components/descriptor-utils";
import { PropertySection } from "../components/property-sections";
import { getSourceColumnProperty, makeEnumProperty } from "../components/property-utils";
import {
    ActionDescription,
    ActionKind,
    ColumnAssignmentsDescription,
    InputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    SourceColumn,
    TableColumn,
    TableGlideType,
    isSingleRelationType,
    makeInputOutputTables,
    getTableColumnDisplayName,
    ColumnAssignment,
    getTableName,
} from "../description";
import { getColumnAssignments } from "../description-utils";
import { LetBinding, SerializedElementBuilder } from "../serialized-component";
import { getColumnForSourceColumn } from "../source-column";
import { ActionDescriptor } from "./action-descriptor";
import { DescriptionToken } from "./action-handler";
import { makeValueGettersForTable } from "./add-row";
import { BaseActionHandler } from "./base";

export interface SetColumnsActionDescription extends ActionDescription, ColumnAssignmentsDescription {
    readonly kind: ActionKind.SetColumns;
    readonly outputRow: PropertyDescription;
}

// `true` means "this row", `undefined` means error
export function getOutputColumn(
    outputRow: PropertyDescription,
    tables: InputOutputTables,
    ccc: ComponentConfiguratorContext
): { sourceColumn: SourceColumn; column: TableColumn; table: TableGlideType } | true | undefined {
    const sourceColumn = getSourceColumnProperty(outputRow);
    if (sourceColumn === undefined) return true;

    const column = getColumnForSourceColumn(ccc, sourceColumn, tables.output, undefined);
    if (column === undefined || !isSingleRelationType(column.type)) return undefined;

    const table = ccc.findTable(column.type);
    if (table === undefined) return undefined;

    return { sourceColumn, column, table };
}

export function getDestinationRowGetter(
    ib: WireInflationBackend,
    desc: PropertyDescription
): { destTable: TableGlideType; destRowGetter: WireValueGetter } | undefined {
    let destTable: TableGlideType;
    let destRowGetter: WireValueGetter;

    if (getSourceColumnProperty(desc) !== undefined) {
        const [getter, type] = ib.getValueGetterForProperty(desc, false, { inOutputRow: true });
        if (getter === undefined || !isSingleRelationType(type)) return undefined;
        const table = ib.findTable(type);
        if (table === undefined) return undefined;

        destTable = table;
        destRowGetter = getter;
    } else {
        destTable = ib.tables.output;
        destRowGetter = vp => vp.rowContext?.outputRow;
    }

    return { destTable, destRowGetter };
}

export function withOutputContextForwardee<T>(
    b: SerializedElementBuilder,
    outputContext: SerializedComponent,
    f: (lets: readonly LetBinding[], outputContext: SerializedComponent) => T
): T {
    const lets: LetBinding[] = [["outputForwardee", b.getForwardee(outputContext)]];
    return f(lets, b.getVariable("outputForwardee"));
}

export class SetColumnsActionHandler extends BaseActionHandler<SetColumnsActionDescription> {
    public readonly kind = ActionKind.SetColumns;
    // FIXME: Proper icon
    public readonly iconName = "plus-circle";

    public getDescriptor(
        desc: SetColumnsActionDescription | undefined,
        tables: InputOutputTables | undefined,
        ccc: ComponentConfiguratorContext
    ): ActionDescriptor {
        const propertyDescriptors: PropertyDescriptor[] = [
            makeSingleRelationOrThisItemPropertyDescriptor("outputRow", "Row", PropertySection.Data),
        ];
        if (tables !== undefined) {
            let assignmentTables = tables;
            if (desc !== undefined) {
                const tableAndColumn = getOutputColumn(desc.outputRow, tables, ccc);
                if (tableAndColumn !== true && tableAndColumn !== undefined) {
                    assignmentTables = makeInputOutputTables(tables.input, tableAndColumn.table);
                }
            }

            propertyDescriptors.push(
                ...makePropertyDescriptorsForColumns<SetColumnsActionDescription>(
                    assignmentTables,
                    false,
                    true,
                    true,
                    true,
                    getColumnAssignments,
                    columns => ({ columnAssignments: columns })
                )[0]
            );
        }
        return {
            name: "Set Column Values",
            isLegacy: !getFeatureSetting("addAndSetActions"),
            needsScreenContext: true,
            properties: propertyDescriptors,
        };
    }

    public newActionDescription(_tables: InputOutputTables): SetColumnsActionDescription {
        return {
            kind: this.kind,
            columnAssignments: [],
            outputRow: makeEnumProperty(true),
        };
    }

    public getColumnAssignments(
        ccc: ComponentConfiguratorContext,
        desc: SetColumnsActionDescription,
        tables: InputOutputTables
    ): { assignments: readonly ColumnAssignment[]; table: TableGlideType } | undefined {
        const tableAndColumn = getOutputColumn(desc.outputRow, tables, ccc);
        if (tableAndColumn === undefined) return undefined;

        let table: TableGlideType;
        if (tableAndColumn !== true) {
            table = tableAndColumn.table;
        } else {
            table = tables.output;
        }

        return { assignments: getColumnAssignments(desc), table };
    }

    public makeAction(
        builder: ComponentBuilder,
        desc: SetColumnsActionDescription,
        tables: InputOutputTables,
        screenContext: SerializedComponent | undefined,
        outputContext: SerializedComponent,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): SerializedComponent | undefined {
        const { b } = builder;

        if (screenContext === undefined) {
            return panic("Must have screen context for set columns action");
        }

        return withOutputContextForwardee(b, outputContext, (outputLets, outputForwardee) => {
            const tableAndColumn = getOutputColumn(desc.outputRow, tables, builder);
            const maybeAssignments = this.getColumnAssignments(builder, desc, tables);
            if (tableAndColumn === undefined || maybeAssignments === undefined) return undefined;

            const { assignments, table: outputTable } = maybeAssignments;

            if (tableAndColumn !== true) {
                const columnValue = makeValueForSourceColumn(
                    builder,
                    tableAndColumn.sourceColumn,
                    outputForwardee,
                    tables.output,
                    undefined,
                    undefined,
                    false
                );
                if (columnValue === undefined) return undefined;

                outputForwardee = b.getForwardee(columnValue.value);
            }

            const columnValues = builder.makeColumnValues(
                makeInputOutputTables(tables.input, outputTable),
                assignments,
                undefined,
                [],
                screenContext,
                true,
                true
            );

            const outerLets: LetBinding[] = [
                ["cvs", b.object(columnValues)],
                ["out", outputForwardee],
            ];

            const sequences: SerializedComponent[] = [];

            const setColumnData: Record<string, SetColumnData> = {};
            const indirectChange = mutatingScreenKind !== undefined && tableAndColumn === true;
            const columnSource = indirectChange ? b.getVariable("out") : b.getVariable("cvs");
            for (const columnName of Object.keys(columnValues)) {
                setColumnData[columnName] = {
                    propertyValue: b.getProperty(columnName, columnSource, outputTable),
                    defaultValue: undefined,
                };
            }

            const setColumnsResult = setColumnsInRowForEditComponent(
                b,
                mutatingScreenKind,
                setColumnData,
                outputTable,
                undefined,
                b.getVariable("out"),
                { withDiode: false, debounce: true }
            );
            if (setColumnsResult === undefined) return undefined;

            const { lets, valuesToModify, action, isEnabled } = setColumnsResult;

            const actions: SerializedComponent[] = [];

            // This is redundant for non-mutating screens, but necessary for mutating screens.
            // One major caveat: we don't want to do this if the target is a relation.
            if (indirectChange) {
                for (const columnName of Object.keys(columnValues)) {
                    const valueToModify = defined(valuesToModify[columnName]);
                    actions.push(b.set(valueToModify, b.getNonTableProperty(columnName, b.getVariable("cvs"))));
                }
            }
            if (action !== undefined) {
                actions.push(action);
            }
            let sequence = b.sequence(actions);
            if (isEnabled !== undefined) {
                sequence = b.if(isEnabled, sequence);
            }
            sequences.push(b.let(lets, sequence));

            return b.holdAction(b.let([...outputLets, ...outerLets], b.sequence(sequences)));
        });
    }

    public getEditedColumns(
        desc: SetColumnsActionDescription,
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): EditedColumns | undefined {
        const editedColumns = super.getEditedColumns(desc, tables, ccc, mutatingScreenKind);
        if (editedColumns === undefined) return undefined;

        const tableAndColumn = getOutputColumn(desc.outputRow, tables, ccc);
        if (tableAndColumn !== true) return editedColumns;

        const assignments = getColumnAssignments(desc);
        const tableName = getTableName(tables.output);
        const assignmentsEdited: EditedColumns = assignments.map(a => [a.destColumn, true, tableName]);
        return [...editedColumns, ...assignmentsEdited];
    }

    public getAllowedDataEdits(
        desc: SetColumnsActionDescription,
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext
    ): AllowedDataEdits | undefined {
        const tableAndColumn = getOutputColumn(desc.outputRow, tables, ccc);
        if (tableAndColumn === undefined) return undefined;

        const outputTable = tableAndColumn === true ? tables.output : tableAndColumn.table;

        const assignments = getColumnAssignments(desc);
        return {
            addRowToTable: new DefaultMap(() => new Set()),
            setColumnsInRow: new DefaultMap(
                () => new Set(),
                [[outputTable, new Set(assignments.map(a => a.destColumn))]]
            ),
            deleteRow: new Set(),
        };
    }

    public getTokenizedDescription(
        desc: SetColumnsActionDescription,
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext
    ): readonly DescriptionToken[] | undefined {
        const outputColumn = getOutputColumn(desc.outputRow, tables, ccc);
        if (outputColumn === undefined) return undefined;
        if (outputColumn === true) return [{ kind: "string", value: "in this row" }];
        return [
            { kind: "string", value: "in " },
            { kind: "column", value: getTableColumnDisplayName(outputColumn.column) },
        ];
    }

    public inflate(ib: WireInflationBackend, desc: SetColumnsActionDescription): WireActionHydrator | undefined {
        const destination = getDestinationRowGetter(ib, desc.outputRow);
        if (destination === undefined) return undefined;
        const { destTable, destRowGetter } = destination;

        const outputValueGetters = makeValueGettersForTable(ib, getColumnAssignments(desc), destTable);
        if (outputValueGetters.length === 0) return undefined;

        return vp => {
            const destRow = destRowGetter(vp);
            if (!isRow(destRow)) return undefined;

            const updates = fromPairs(outputValueGetters.map(([n, g]) => [n, g(vp)]));
            if (Object.keys(updates).length === 0) return undefined;

            return async ab => {
                await ab.setColumnsInRow(getTableName(destTable), destRow, updates);
                return true;
            };
        };
    }
}
